import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Table, Spin, Row, Col, Progress, Flex, Modal, Tooltip, Select, Input, message, Tag } from 'antd';
import { DownloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, RadarChartOutlined, ClearOutlined, ProfileOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { getIppta, deleteIppta } from '../../services/ippta/get_ippta';
import { selectAppPermissions } from '../../store/auth/selectors';
import { useSelector } from 'react-redux';
import IpptaForm from './IpptaForm';
import IpptaRadar from './IpptaRadar';
import { getProyectos } from '../../services/ippta/get_dataIppta';
import IpptaExport from './IpptaExport';
import { getIpptaExportable } from '../../services/ippta/get_ippta';
import PlanAccion from '../Ippta/PlanAccion';
import { useParams } from "react-router-dom";
import FileManagerPOA from '../Poa/FileManagerPOA.jsx';

const twoColors = { '0%': '#108ee9', '100%': '#87d068' };
const { Option } = Select;

// 1 = Inicial, 2 = Final  (int en BD)
const MEDICION_LABELS = { 1: 'Inicial', 2: 'Final' };

const toTitleCase = (str) => {
  if (!str) return str;
  return String(str)
    .toLowerCase()
    .replace(/(^|[^\p{L}])(\p{L})/gu, (m, sep, letter) => sep + letter.toUpperCase());
};

const Ippta = ({ proyectoCodigo }) => {
  const { id } = useParams();
  const proyectoFiltrar = proyectoCodigo || id || "";

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setStatus] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [formData, setFormData] = useState(null);
  const [identificacionGrafica, setIdentificacionGrafica] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [dataExport, setDataExport] = useState(null);
  const [identificacionFilter, setIdentificacionFilter] = useState('');
  const [proyectoFilter, setProyectoFilter] = useState('');
  const [proyectos, setProyectos] = useState([]);
  const [oidRegistro, setOidRegistro] = useState(null);
  const [showPlanAccion, setShowPlanAccion] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const appName = 'sistematizacionIppta';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user.username;
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionExport = permissions.priv_export === 'Y';
  const permissionDelete = permissions.priv_delete === 'Y';
  const [isExporting, setIsExporting] = useState(false);
  const [itemParams, setItemParams] = useState(null);
  const [openModalVisitasEcas, setOpenModalVisitasEcas] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState('');
  // medicion se guarda como int: null = sin filtro, 1 = Inicial, 2 = Final
  const [medicionFilter, setMedicionFilter] = useState(null);

  // ── Carga inicial: proyectos + registros en paralelo ──────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [proyectosData, result] = await Promise.all([
        getProyectos(),
        getIppta(),
      ]);
      setProyectos(proyectosData.data || []);

      const combinedData = result.data.map(item => ({
        ...item,
        codigoProyecto: `${item.codigo} - ${item.proyecto}`,
        medicionGeneral: `${Math.round(
          ((item.productiva / 35) + (item.socioeconomico / 25) + (item.ambiental / 30)) / 3 * 100
        )}%`,
      }));
      setDataSource(combinedData);
      setStatus(null);
    } catch (error) {
      setStatus(error.response ? error.response.status : 500);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Carga exportable (independiente, no bloquea la tabla) ─────────────────
  useEffect(() => {
    let cancelled = false;
    const loadExport = async () => {
      try {
        const response = await getIpptaExportable();
        if (!cancelled) setDataExport(response.data);
      } catch (error) {
        console.error("Error al obtener datos exportables:", error);
      }
    };
    loadExport();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (proyectoFiltrar) setProyectoFilter(proyectoFiltrar);
  }, [proyectoFiltrar]);

  // ── Datos filtrados (memoizados — no recalcula en renders sin cambio) ─────
  const filteredData = useMemo(() => dataSource.filter(item =>
    (identificacionFilter === '' || item.identificacion.includes(identificacionFilter)) &&
    (proyectoFilter === '' || item.codigo === proyectoFilter) &&
    (estadoFilter === '' || item.estado_participante === estadoFilter) &&
    (medicionFilter === null || item.medicion === medicionFilter)
  ), [dataSource, identificacionFilter, proyectoFilter, estadoFilter, medicionFilter]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleEdit = useCallback((record) => {
    setFormData(record);
    setIsModalVisible(true);
  }, []);

  const handleChart = useCallback((record) => {
    setIdentificacionGrafica(record.identificacion);
    setShowRadar(true);
  }, []);

  const handPlanAccion = useCallback((record) => {
    setOidRegistro(record.codigo_ippta);
    setShowPlanAccion(true);
  }, []);

  const showModal = useCallback(() => {
    setFormData(null);
    setIsModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setFormData(null);
    setIsModalVisible(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setIdentificacionFilter('');
    setProyectoFilter('');
    setEstadoFilter('');
    setMedicionFilter(null);
  }, []);

  // Elimina el item localmente para evitar el spinner de recarga completa
  const handleDelete = useCallback((record) => {
    Modal.confirm({
      title: "¿Eliminar registro?",
      content: `Esta acción eliminará el registro del participante: ${record.participante}`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteIppta(record.codigo_ippta, userLogin);
          message.success("Registro eliminado correctamente");
          setDataSource(prev => prev.filter(item => item.codigo_ippta !== record.codigo_ippta));
        } catch (error) {
          message.error("Error eliminando el registro");
        }
      },
    });
  }, [userLogin]);

  const exportToExcel = useCallback(async () => {
    if (!dataExport || dataExport.length === 0) {
      console.warn('No hay datos para exportar.');
      return;
    }

    const filteredDataExcel = dataExport.filter(item =>
      (identificacionFilter === '' || item.identificacion.includes(identificacionFilter)) &&
      (proyectoFilter === '' || item.codigo === proyectoFilter) &&
      (estadoFilter === '' || item.estado_participante === estadoFilter) &&
      (medicionFilter === null || item.medicion === medicionFilter)
    );

    if (filteredDataExcel.length === 0) {
      console.warn('No hay datos que coincidan con los filtros.');
      return;
    }

    const updatedData = filteredDataExcel.map(item => {
      const calcularSuma = (prefijo, cantidad) =>
        Array.from({ length: cantidad }, (_, i) => item[`${prefijo}_indicador${i + 1}`] || 0)
          .reduce((acc, curr) => acc + curr, 0);

      const sumaProductivo = calcularSuma('productivo', 7);
      const sumaSocioeconomico = calcularSuma('socioeconomico', 5);
      const sumaAmbiental = calcularSuma('ambiental', 6);

      return {
        ...item,
        porcentajeProductivo: ((sumaProductivo / 35) * 100).toFixed(2) + '%',
        porcentajeSocioeconomico: ((sumaSocioeconomico / 25) * 100).toFixed(2) + '%',
        porcentajeAmbiental: ((sumaAmbiental / 30) * 100).toFixed(2) + '%',
        porcentajeTotal: (((sumaProductivo / 35) + (sumaSocioeconomico / 25) + (sumaAmbiental / 30)) / 3 * 100).toFixed(2) + '%',
      };
    });

    setExportData([...updatedData]);
    setTimeout(() => setIsExporting(true), 100);
  }, [dataExport, identificacionFilter, proyectoFilter, estadoFilter, medicionFilter]);

  const handleTableChange = useCallback((pag) => {
    setPagination({ current: pag.current, pageSize: pag.pageSize });
  }, []);

  const handleInforme = useCallback(() => {
    setItemParams({
      proyectoEdah: proyectoFiltrar || "todos",
      nuevaRuta: true,
      rutaInformacionGeneral: "informes_ippta",
    });
    setOpenModalVisitasEcas(true);
  }, [proyectoFiltrar]);

  const hideModalVisitasEcas = useCallback(() => {
    setOpenModalVisitasEcas(false);
    setItemParams(null);
  }, []);

  // ── Columnas (memoizadas) ─────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'left',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '30px', justifyContent: 'center' }}>
          <Tooltip title="Editar">
            <span onClick={() => handleEdit(record)} style={{ cursor: 'pointer', color: '#1890ff' }}>
              <EditOutlined />
            </span>
          </Tooltip>
          <Tooltip title="Plan de acción">
            <span
              onClick={() => handPlanAccion(record)}
              style={{ cursor: 'pointer', color: record.plan_accion === 1 ? 'green' : 'red' }}
            >
              <ProfileOutlined />
            </span>
          </Tooltip>
          <Tooltip title="Ver gráfico">
            <span onClick={() => handleChart(record)} style={{ cursor: 'pointer', color: '#1890ff' }}>
              <RadarChartOutlined />
            </span>
          </Tooltip>
          {permissionDelete && (
            <Tooltip title="Eliminar">
              <span onClick={() => handleDelete(record)} style={{ cursor: 'pointer', color: 'red' }}>
                <DeleteOutlined />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Fecha Registro',
      dataIndex: 'fecha',
      key: 'fecha',
      responsive: ['md'],
    },
    {
      title: 'Proyecto',
      dataIndex: 'codigoProyecto',
      key: 'codigoProyecto',
      responsive: ['md'],
    },
    {
      title: 'Identificación',
      dataIndex: 'identificacion',
      key: 'identificacion',
      responsive: ['md'],
    },
    {
      title: 'Participante',
      dataIndex: 'participante',
      key: 'participante',
      responsive: ['sm'],
      render: (text) => toTitleCase(text),
    },
    {
      title: 'Vereda',
      dataIndex: 'vereda',
      key: 'vereda',
      responsive: ['md'],
      render: (text) => toTitleCase(text),
    },
    {
      title: 'Municipio',
      dataIndex: 'municipio',
      key: 'municipio',
      responsive: ['lg'],
      render: (text) => toTitleCase(text),
    },
    {
      title: 'Departamento',
      dataIndex: 'departamento',
      key: 'departamento',
      responsive: ['md'],
      render: (text) => toTitleCase(text),
    },
    {
      title: 'Medición',
      dataIndex: 'medicion',
      key: 'medicion',
      align: 'center',
      responsive: ['md'],
      render: (val) => {
        const label = MEDICION_LABELS[val];
        const color = val === 1 ? 'blue' : val === 2 ? 'green' : 'default';
        return label ? <Tag color={color}>{label}</Tag> : null;
      },
    },
    {
      title: 'Estado participante',
      dataIndex: 'estado_participante',
      key: 'estado_participante',
      width: 120,
      align: 'center',
      render: (estado) => {
        const colorMap = { 'Activo': 'green', 'Inactivo': 'red', 'N/A': 'default' };
        return <Tag color={colorMap[estado] || 'default'}>{estado}</Tag>;
      },
    },
    {
      title: 'Medición General',
      dataIndex: 'medicionGeneral',
      key: 'medicionGeneral',
      responsive: ['lg'],
      render: (text) => {
        const percent = parseInt(text.replace('%', ''), 10);
        return (
          <Flex vertical gap="middle" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Progress
              percent={percent}
              strokeColor={twoColors}
              format={() => `${percent}%`}
            />
          </Flex>
        );
      },
    },
  ], [pagination, permissionDelete, handleEdit, handPlanAccion, handleChart, handleDelete]);

  if (loading) {
    return <Spin fullscreen tip="Cargando..." />;
  }

  return (
    <div className="container">
      <React.Fragment>
        <div className="row mb-2 d-flex align-items-center">
          <div className="col-md-8 linea_separador mb-2 d-flex align-items-center">
            <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
              <p>PROYECTO UTF <br /> COL 160 COL</p>
            </div>
            <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
              <p>Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET</p>
            </div>
          </div>
          <div className="col-md-4 d-flex justify-content-center align-items-center flex-column">
            <h2 className="text-center mb-2">IPPTA</h2>
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="input-group shadow-sm">
                  {permissionInsert && (
                    <React.Fragment>
                      <Button
                        type="danger"
                        className="btn btn-primary me-2"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={showModal}
                      >
                        Nueva
                      </Button>
                      <IpptaForm
                        visible={isModalVisible}
                        onClose={handleCloseModal}
                        formData={formData}
                        loadData={fetchData}
                      />
                    </React.Fragment>
                  )}
                  {permissionExport && (
                    <Button
                      type="danger"
                      className="btn btn-primary me-2"
                      size="large"
                      onClick={exportToExcel}
                      icon={<DownloadOutlined />}
                      disabled={isExporting}
                    >
                      {isExporting ? 'Exportando...' : 'Exportar a Excel'}
                    </Button>
                  )}
                  {isExporting && <IpptaExport data={exportData} onFinish={() => setIsExporting(false)} />}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Filtros ── */}
        <Row gutter={[12, 12]} style={{ marginBottom: '20px' }} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Filtrar por Identificación"
              value={identificacionFilter}
              onChange={(e) => setIdentificacionFilter(e.target.value)}
              style={{ height: '40px' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%', height: '40px' }}
              placeholder="Filtrar por proyecto"
              showSearch
              value={proyectoFilter || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => setProyectoFilter(value)}
              allowClear
            >
              {proyectos?.map(proyecto => (
                <Option key={proyecto.proyecto_oid} value={proyecto.proyecto_codigo}>
                  {`${proyecto.proyecto_codigo} - ${proyecto.nombre_proyecto}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Select
              style={{ width: '100%', height: '40px' }}
              placeholder="Medición"
              value={medicionFilter ?? undefined}
              onChange={(value) => setMedicionFilter(value ?? null)}
              allowClear
            >
              <Option value={1}>Inicial</Option>
              <Option value={2}>Final</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={3}>
            <Select
              style={{ width: '100%', height: '40px' }}
              placeholder="Estado"
              value={estadoFilter || undefined}
              onChange={(value) => setEstadoFilter(value)}
              allowClear
            >
              <Option value="Activo">Activo</Option>
              <Option value="Inactivo">Inactivo</Option>
              <Option value="N/A">N/A</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4} md={2}>
            <Button
              type="default"
              onClick={handleClearFilters}
              icon={<ClearOutlined />}
              style={{ height: '40px', width: '100%' }}
            >
              Limpiar
            </Button>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Button
              type="primary"
              icon={<FolderOpenOutlined />}
              style={{ height: '40px', width: '100%' }}
              onClick={handleInforme}
            >
              Informe
            </Button>
          </Col>
        </Row>

        <div>
          <div className="participant-count-container">
            <h1 className="participant-count-title">
              Número de Registros: <span className="participant-count-number">{filteredData.length}</span>
            </h1>
          </div>
          <Col span={24}>
            <Table
              className="custom-ippta-table"
              dataSource={filteredData}
              columns={columns}
              rowKey="codigo_ippta"
              pagination={{ current: pagination.current, pageSize: pagination.pageSize }}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
            />
          </Col>
        </div>
      </React.Fragment>

      <Modal
        title={<h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>Gráfico IPPTA</h3>}
        open={showRadar}
        width={1200}
        styles={{ body: { height: '1100px' } }}
        onCancel={() => setShowRadar(false)}
        footer={null}
      >
        <IpptaRadar formData={identificacionGrafica} />
      </Modal>

      <Modal
        title={<h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>PLAN DE ACCIÓN PREDIAL PARA LA TRANSICIÓN AGROECOLÓGICA</h3>}
        open={showPlanAccion}
        width={1200}
        onCancel={() => setShowPlanAccion(false)}
        footer={null}
      >
        <PlanAccion oidRegistro={oidRegistro} />
      </Modal>

      {openModalVisitasEcas && (
        <Modal
          title={<span className="custom-modal-title">Medios de verificación</span>}
          open={openModalVisitasEcas}
          onCancel={hideModalVisitasEcas}
          footer={null}
          destroyOnHidden={true}
          width="95%"
          style={{ top: 20 }}
        >
          <FileManagerPOA
            codigo_tarea={{ codigo_tarea: itemParams }}
            onClose={hideModalVisitasEcas}
          />
        </Modal>
      )}
    </div>
  );
};

export default Ippta;
